/**
 * RetryService - Advanced retry logic with circuit breaker and error classification
 * 
 * This service provides:
 * - Exponential backoff with jitter
 * - Circuit breaker pattern to prevent cascading failures
 * - Error classification (retryable vs non-retryable)
 * - Different retry strategies for different operation types
 * - Comprehensive analytics and tracking
 * - Configurable retry policies
 * 
 * Follows SOLID principles with clear separation of concerns
 */

import { ComposePostEvents } from './analytics/events';

/**
 * Error classification utility
 * Determines if an error should be retried based on type and context
 */
class ErrorClassifier {
    static RETRYABLE_HTTP_CODES = [
        408, // Request Timeout
        429, // Too Many Requests
        500, // Internal Server Error
        502, // Bad Gateway
        503, // Service Unavailable
        504, // Gateway Timeout
        520, // Unknown Error (Cloudflare)
        521, // Web Server Is Down (Cloudflare)
        522, // Connection Timed Out (Cloudflare)
        523, // Origin Is Unreachable (Cloudflare)
        524  // A Timeout Occurred (Cloudflare)
    ];

    static NON_RETRYABLE_HTTP_CODES = [
        400, // Bad Request
        401, // Unauthorized
        403, // Forbidden
        404, // Not Found
        405, // Method Not Allowed
        409, // Conflict
        422, // Unprocessable Entity
        451  // Unavailable For Legal Reasons
    ];

    static RETRYABLE_ERROR_TYPES = [
        'NetworkError',
        'TimeoutError',
        'ConnectionError',
        'ServiceUnavailableError'
    ];

    static NON_RETRYABLE_ERROR_TYPES = [
        'ValidationError',
        'AuthenticationError',
        'AuthorizationError',
        'NotFoundError',
        'ConflictError'
    ];

    /**
     * Classify an error as retryable or not
     * @param {Error|Object} error - The error to classify
     * @param {string} operation - The operation type (posting, scheduling, etc.)
     * @returns {Object} Classification result with retry recommendation
     */
    static classify(error, operation = 'unknown') {
        const classification = {
            isRetryable: false,
            errorType: 'unknown',
            httpCode: null,
            reason: '',
            suggestedDelay: 1000,
            maxRetries: 3
        };

        // Handle HTTP errors
        if (error.status || error.statusCode) {
            const httpCode = error.status || error.statusCode;
            classification.httpCode = httpCode;

            if (this.RETRYABLE_HTTP_CODES.includes(httpCode)) {
                classification.isRetryable = true;
                classification.errorType = 'http_retryable';
                classification.reason = `HTTP ${httpCode} is retryable`;
                
                // Adjust strategy based on error type
                if (httpCode === 429) { // Rate limiting
                    classification.suggestedDelay = 5000; // 5 seconds
                    classification.maxRetries = 5;
                } else if (httpCode >= 500) { // Server errors
                    classification.suggestedDelay = 2000; // 2 seconds
                    classification.maxRetries = 4;
                }
            } else if (this.NON_RETRYABLE_HTTP_CODES.includes(httpCode)) {
                classification.isRetryable = false;
                classification.errorType = 'http_non_retryable';
                classification.reason = `HTTP ${httpCode} is not retryable`;
            } else {
                // Unknown HTTP code - be conservative
                classification.isRetryable = httpCode >= 500;
                classification.errorType = 'http_unknown';
                classification.reason = `HTTP ${httpCode} classification unknown`;
            }
        }

        // Handle network/connection errors
        if (error.name && this.RETRYABLE_ERROR_TYPES.includes(error.name)) {
            classification.isRetryable = true;
            classification.errorType = error.name.toLowerCase();
            classification.reason = `${error.name} is retryable`;
        } else if (error.name && this.NON_RETRYABLE_ERROR_TYPES.includes(error.name)) {
            classification.isRetryable = false;
            classification.errorType = error.name.toLowerCase();
            classification.reason = `${error.name} is not retryable`;
        }

        // Handle specific error messages
        const errorMessage = error.message?.toLowerCase() || '';
        if (errorMessage.includes('network') || 
            errorMessage.includes('timeout') || 
            errorMessage.includes('connection')) {
            classification.isRetryable = true;
            classification.errorType = 'network';
            classification.reason = 'Network-related error detected';
        }

        // Operation-specific adjustments
        if (operation === 'posting' && classification.isRetryable) {
            classification.maxRetries = 3; // Conservative for posting
        } else if (operation === 'scheduling' && classification.isRetryable) {
            classification.maxRetries = 5; // More retries for scheduling
        }

        return classification;
    }
}

/**
 * Circuit breaker implementation to prevent cascading failures
 */
class CircuitBreaker {
    constructor(options = {}) {
        this.failureThreshold = options.failureThreshold || 5;
        this.recoveryTimeout = options.recoveryTimeout || 60000; // 1 minute
        this.monitoringPeriod = options.monitoringPeriod || 10000; // 10 seconds
        
        this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
        this.failureCount = 0;
        this.lastFailureTime = null;
        this.successCount = 0;
        this.requestCount = 0;
        
        this.stateChangeCallbacks = [];
    }

    /**
     * Execute a function with circuit breaker protection
     * @param {Function} fn - Function to execute
     * @param {string} operation - Operation identifier
     * @returns {Promise} Result or circuit breaker error
     */
    async execute(fn, operation = 'unknown') {
        if (this.state === 'OPEN') {
            if (Date.now() - this.lastFailureTime >= this.recoveryTimeout) {
                this.state = 'HALF_OPEN';
                this.notifyStateChange('HALF_OPEN', operation);
            } else {
                throw new Error(`Circuit breaker is OPEN for ${operation}`);
            }
        }

        this.requestCount++;

        try {
            const result = await fn();
            this.onSuccess(operation);
            return result;
        } catch (error) {
            this.onFailure(error, operation);
            throw error;
        }
    }

    onSuccess(operation) {
        this.successCount++;
        
        if (this.state === 'HALF_OPEN') {
            this.state = 'CLOSED';
            this.failureCount = 0;
            this.notifyStateChange('CLOSED', operation);
        }
    }

    onFailure(error, operation) {
        this.failureCount++;
        this.lastFailureTime = Date.now();

        if (this.failureCount >= this.failureThreshold) {
            this.state = 'OPEN';
            this.notifyStateChange('OPEN', operation, error);
        }
    }

    notifyStateChange(newState, operation, error = null) {
        this.stateChangeCallbacks.forEach(callback => {
            try {
                callback(newState, operation, error, this.getStats());
            } catch (err) {
                console.error('Circuit breaker callback error:', err);
            }
        });
    }

    onStateChange(callback) {
        this.stateChangeCallbacks.push(callback);
    }

    getStats() {
        return {
            state: this.state,
            failureCount: this.failureCount,
            successCount: this.successCount,
            requestCount: this.requestCount,
            failureRate: this.requestCount > 0 ? this.failureCount / this.requestCount : 0,
            lastFailureTime: this.lastFailureTime
        };
    }

    reset() {
        this.state = 'CLOSED';
        this.failureCount = 0;
        this.successCount = 0;
        this.requestCount = 0;
        this.lastFailureTime = null;
    }
}

/**
 * Main RetryService class
 */
class RetryService {
    constructor(trackingHandler, options = {}) {
        this.trackingHandler = trackingHandler;
        
        // Default configuration
        this.config = {
            defaultMaxRetries: 3,
            defaultBaseDelay: 1000,
            defaultMaxDelay: 30000,
            circuitBreaker: {
                enabled: options.enableCircuitBreaker !== false,
                failureThreshold: options.failureThreshold || 5,
                recoveryTimeout: options.recoveryTimeout || 60000
            }
        };

        // Initialize circuit breakers per operation type
        this.circuitBreakers = {};
        if (this.config.circuitBreaker.enabled) {
            const operations = ['posting', 'scheduling', 'threading', 'bucketAssignment'];
            operations.forEach(operation => {
                this.circuitBreakers[operation] = new CircuitBreaker(this.config.circuitBreaker);
                
                // Track circuit breaker state changes
                this.circuitBreakers[operation].onStateChange((state, op, error, stats) => {
                    this.trackingHandler.trackEvent('circuit_breaker_state_change', {
                        operation: op,
                        newState: state,
                        failureCount: stats.failureCount,
                        failureRate: stats.failureRate,
                        error: error?.message
                    });
                });
            });
        }
    }

    /**
     * Execute a function with retry logic
     * @param {Function} fn - Function to execute
     * @param {Object} options - Retry options
     * @returns {Promise} Result or final error
     */
    async executeWithRetry(fn, options = {}) {
        const {
            operation = 'unknown',
            maxRetries = this.config.defaultMaxRetries,
            baseDelay = this.config.defaultBaseDelay,
            maxDelay = this.config.defaultMaxDelay,
            onRetry = null,
            context = {}
        } = options;

        const circuitBreaker = this.circuitBreakers[operation];
        
        let lastError = null;
        let attempt = 0;

        // Track operation start
        const startTime = Date.now();
        this.trackingHandler.trackEvent('retry_operation_start', {
            operation,
            maxRetries,
            circuitBreakerEnabled: !!circuitBreaker
        });

        while (attempt <= maxRetries) {
            try {
                // Execute with circuit breaker if enabled
                const result = circuitBreaker 
                    ? await circuitBreaker.execute(fn, operation)
                    : await fn();

                // Track success
                if (attempt > 0) {
                    this.trackingHandler.trackEvent('retry_success', {
                        operation,
                        totalAttempts: attempt + 1,
                        totalDuration: Date.now() - startTime
                    });
                }

                return result;

            } catch (error) {
                lastError = error;
                attempt++;

                // Classify the error
                const classification = ErrorClassifier.classify(error, operation);
                
                // Track retry attempt
                this.trackingHandler.trackEvent('retry_attempt', {
                    operation,
                    attempt,
                    maxRetries,
                    errorType: classification.errorType,
                    errorCode: classification.httpCode,
                    isRetryable: classification.isRetryable,
                    errorMessage: error.message
                });

                // Check if we should retry
                if (!classification.isRetryable || attempt > maxRetries) {
                    // Track final failure
                    this.trackingHandler.trackEvent('retry_exhausted', {
                        operation,
                        totalAttempts: attempt,
                        totalDuration: Date.now() - startTime,
                        finalError: error.message,
                        errorType: classification.errorType,
                        isRetryable: classification.isRetryable
                    });
                    
                    throw error;
                }

                // Calculate delay for next attempt (exponential backoff with jitter)
                const exponentialDelay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
                const jitter = exponentialDelay * 0.1 * Math.random();
                const delay = Math.floor(exponentialDelay + jitter);
                
                // Notify retry callback if provided
                if (onRetry) {
                    try {
                        await onRetry(error, attempt, delay, classification);
                    } catch (callbackError) {
                        console.error('Retry callback error:', callbackError);
                    }
                }

                // Wait before next attempt
                if (delay > 0) {
                    await this.sleep(delay);
                }
            }
        }

        // This should never be reached, but included for completeness
        throw lastError;
    }

    /**
     * Sleep for specified duration
     * @param {number} ms - Milliseconds to sleep
     * @returns {Promise} Promise that resolves after delay
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Get circuit breaker stats for an operation
     * @param {string} operation - Operation type
     * @returns {Object} Circuit breaker statistics
     */
    getCircuitBreakerStats(operation) {
        const breaker = this.circuitBreakers[operation];
        return breaker ? breaker.getStats() : null;
    }

    /**
     * Reset circuit breaker for an operation
     * @param {string} operation - Operation type
     */
    resetCircuitBreaker(operation) {
        const breaker = this.circuitBreakers[operation];
        if (breaker) {
            breaker.reset();
            this.trackingHandler.trackEvent('circuit_breaker_reset', {
                operation
            });
        }
    }

    /**
     * Get overall retry service statistics
     * @returns {Object} Service statistics
     */
    getStats() {
        const stats = {
            circuitBreakers: {}
        };

        Object.keys(this.circuitBreakers).forEach(operation => {
            stats.circuitBreakers[operation] = this.getCircuitBreakerStats(operation);
        });

        return stats;
    }
}

/**
 * Factory function to create RetryService instances
 * @param {Object} trackingHandler - Analytics tracking handler
 * @param {Object} options - Configuration options
 * @returns {RetryService} Configured retry service instance
 */
export const createRetryService = (trackingHandler, options = {}) => {
    return new RetryService(trackingHandler, options);
};

export {
    RetryService,
    ErrorClassifier,
    CircuitBreaker
};

export default RetryService; 